/**
 * Coups de pouce — Attendu 653 « Distance entre deux points ».
 *
 * Une entrée par `generator_key` (cf. gen_653.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : les 3 notations côte à côte ──
// Lettres M/N et cote 12 cm : gen_653 tire n ∈ [2..9], aucune collision possible.
const SVG_NOTATIONS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 186" role="img"
     aria-label="Le segment MN, la droite MN et la longueur MN">
  <!-- ① [MN] : le segment -->
  <text x="8" y="34" font-size="15" font-weight="700" fill="#1E3A8A">[MN]</text>
  <line x1="120" y1="28" x2="270" y2="28" stroke="#1E3A8A" stroke-width="3"/>
  <line x1="120" y1="20" x2="120" y2="36" stroke="#1E3A8A" stroke-width="3"/>
  <line x1="270" y1="20" x2="270" y2="36" stroke="#1E3A8A" stroke-width="3"/>
  <text x="120" y="14" text-anchor="middle" font-size="13" fill="#0f172a">M</text>
  <text x="270" y="14" text-anchor="middle" font-size="13" fill="#0f172a">N</text>
  <text x="8" y="50" font-size="10" fill="#64748b">le segment</text>

  <!-- ② (MN) : la droite -->
  <text x="8" y="100" font-size="15" font-weight="700" fill="#1E3A8A">(MN)</text>
  <line x1="98" y1="94" x2="292" y2="94" stroke="#1E3A8A" stroke-width="3"/>
  <polygon points="88,94 100,89 100,99" fill="#1E3A8A"/>
  <polygon points="302,94 290,89 290,99" fill="#1E3A8A"/>
  <circle cx="120" cy="94" r="3.5" fill="#1E3A8A"/>
  <circle cx="270" cy="94" r="3.5" fill="#1E3A8A"/>
  <text x="120" y="80" text-anchor="middle" font-size="13" fill="#0f172a">M</text>
  <text x="270" y="80" text-anchor="middle" font-size="13" fill="#0f172a">N</text>
  <text x="8" y="116" font-size="10" fill="#64748b">la droite</text>

  <!-- ③ MN : la longueur (un nombre) -->
  <text x="8" y="166" font-size="15" font-weight="700" fill="#F97316">MN</text>
  <line x1="120" y1="172" x2="270" y2="172" stroke="#94a3b8" stroke-width="2"/>
  <line x1="120" y1="164" x2="120" y2="180" stroke="#94a3b8" stroke-width="2"/>
  <line x1="270" y1="164" x2="270" y2="180" stroke="#94a3b8" stroke-width="2"/>
  <polygon points="122,172 132,168 132,176" fill="#F97316"/>
  <polygon points="268,172 258,168 258,176" fill="#F97316"/>
  <line x1="130" y1="172" x2="260" y2="172" stroke="#F97316" stroke-width="2"/>
  <text x="195" y="163" text-anchor="middle" font-size="14" font-weight="700" fill="#F97316">MN = NM = 12 cm</text>
  <text x="8" y="182" font-size="10" fill="#64748b">la longueur</text>
</svg>`.trim();


// ── Figure Ex. 4 : les côtés mis bout à bout font le périmètre ──
// Un triangle, pas un quadrilatère : l'exercice tire les deux, l'exemple prend
// le cas le plus simple. Les trois côtés sont repris dans leur couleur sur la
// demi-droite, pour que la correspondance côté → report se lise sans légende.
const SVG_PERIMETRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 172" role="img"
     aria-label="Les trois côtés du triangle DEF reportés bout à bout sur une demi-droite forment son périmètre">
  <line x1="40" y1="30" x2="120" y2="30" stroke="#1E3A8A" stroke-width="3.5"/>
  <line x1="120" y1="30" x2="72" y2="81" stroke="#29B6E8" stroke-width="3.5"/>
  <line x1="72" y1="81" x2="40" y2="30" stroke="#8B5CF6" stroke-width="3.5"/>
  <text x="34" y="26" text-anchor="end" font-size="13" fill="#0f172a">D</text>
  <text x="126" y="26" font-size="13" fill="#0f172a">E</text>
  <text x="66" y="96" text-anchor="end" font-size="13" fill="#0f172a">F</text>

  <text x="250" y="64" text-anchor="middle" font-size="12" fill="#64748b">Je reporte les longueurs des 3 côtés à la suite.</text>

  <line x1="30" y1="125" x2="352" y2="125" stroke="#94a3b8" stroke-width="1.5"/>
  <polygon points="362,125 350,120 350,130" fill="#94a3b8"/>

  <line x1="30" y1="125" x2="110" y2="125" stroke="#1E3A8A" stroke-width="6"/>
  <line x1="110" y1="125" x2="180" y2="125" stroke="#29B6E8" stroke-width="6"/>
  <line x1="180" y1="125" x2="240" y2="125" stroke="#8B5CF6" stroke-width="6"/>
  <g stroke="#0f172a" stroke-width="1.5">
    <line x1="30" y1="117" x2="30" y2="133"/>
    <line x1="110" y1="117" x2="110" y2="133"/>
    <line x1="180" y1="117" x2="180" y2="133"/>
    <line x1="240" y1="117" x2="240" y2="133"/>
  </g>

  <path d="M30 143 L30 149 L240 149 L240 143" fill="none" stroke="#F97316" stroke-width="1.5"/>
  <text x="135" y="166" text-anchor="middle" font-size="14" font-weight="700" fill="#F97316">périmètre de DEF</text>
</svg>`.trim();

// ── Figure Ex. 5 : les deux positions possibles de G ──
// EF = 12 cm : le générateur tire EF ∈ [4..10], donc hors de portée. Les deux
// cas sont montrés côte à côte, car c'est leur CONTRASTE qui fait la notion :
// même énoncé au mot près, deux places pour G, deux soustractions différentes.
const SVG_POSITION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 216" role="img"
     aria-label="Deux cas : G entre E et F, puis F entre E et G. Les longueurs donnees EF egale 12 cm et EG egale 18 cm sont cotees sur les schemas">
  <text x="10" y="16" font-size="11" font-weight="700" fill="#64748b">G est entre E et F</text>
  <line x1="30" y1="42" x2="290" y2="42" stroke="#1E3A8A" stroke-width="2.5"/>
  <circle cx="30" cy="42" r="4" fill="#1E3A8A"/>
  <circle cx="138" cy="42" r="4" fill="#F97316"/>
  <circle cx="290" cy="42" r="4" fill="#1E3A8A"/>
  <text x="30" y="34" text-anchor="middle" font-size="13" fill="#0f172a">E</text>
  <text x="138" y="34" text-anchor="middle" font-size="13" fill="#F97316">G</text>
  <text x="290" y="34" text-anchor="middle" font-size="13" fill="#0f172a">F</text>
  <text x="84" y="60" text-anchor="middle" font-size="11" fill="#475569">5 cm</text>
  <text x="214" y="60" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">? </text>
  <path d="M30 66 L30 71 L290 71 L290 66" fill="none" stroke="#1E3A8A" stroke-width="1.5"/>
  <text x="160" y="86" text-anchor="middle" font-size="12" font-weight="700" fill="#1E3A8A">EF = 12 cm</text>
  <text x="290" y="103" text-anchor="end" font-size="13" font-weight="700" fill="#F97316">GF = 12 − 5 = 7 cm</text>

  <line x1="10" y1="115" x2="370" y2="115" stroke="#e2e8f0" stroke-width="1"/>

  <text x="10" y="133" font-size="11" font-weight="700" fill="#64748b">F est entre E et G</text>
  <line x1="30" y1="157" x2="290" y2="157" stroke="#1E3A8A" stroke-width="2.5"/>
  <circle cx="30" cy="157" r="4" fill="#1E3A8A"/>
  <circle cx="203" cy="157" r="4" fill="#1E3A8A"/>
  <circle cx="290" cy="157" r="4" fill="#F97316"/>
  <text x="30" y="149" text-anchor="middle" font-size="13" fill="#0f172a">E</text>
  <text x="203" y="149" text-anchor="middle" font-size="13" fill="#0f172a">F</text>
  <text x="290" y="149" text-anchor="middle" font-size="13" fill="#F97316">G</text>
  <text x="116" y="175" text-anchor="middle" font-size="11" fill="#475569">12 cm</text>
  <text x="246" y="175" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">?</text>
  <path d="M30 181 L30 186 L290 186 L290 181" fill="none" stroke="#1E3A8A" stroke-width="1.5"/>
  <text x="160" y="201" text-anchor="middle" font-size="12" font-weight="700" fill="#1E3A8A">EG = 18 cm</text>
  <text x="290" y="214" text-anchor="end" font-size="13" font-weight="700" fill="#F97316">GF = 18 − 12 = 6 cm</text>
</svg>`.trim();

// ── Figure Ex. 6a : l'échelle de conversion, sur la page « La règle » ──
// Limitée à dm / cm / mm : ce sont les seules unités que le générateur produit.
// Y ajouter le mètre alourdirait sans servir.
const SVG_CONVERSION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 150" role="img"
     aria-label="Échelle de conversion : 1 dm égale 10 cm égale 100 mm">
  <g stroke="#F97316" stroke-width="1.8" fill="none">
    <path d="M102 34 L140 34"/>
    <path d="M212 34 L250 34"/>
  </g>
  <polygon points="146,34 136,30 136,38" fill="#F97316"/>
  <polygon points="256,34 246,30 246,38" fill="#F97316"/>
  <text x="123" y="26" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">× 10</text>
  <text x="233" y="26" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">× 10</text>

  <g fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.5">
    <rect x="38" y="44" width="64" height="34" rx="6"/>
    <rect x="148" y="44" width="64" height="34" rx="6"/>
    <rect x="258" y="44" width="64" height="34" rx="6"/>
  </g>
  <g font-size="17" font-weight="700" fill="#1E3A8A" text-anchor="middle">
    <text x="70" y="67">dm</text>
    <text x="180" y="67">cm</text>
    <text x="290" y="67">mm</text>
  </g>

  <g stroke="#64748b" stroke-width="1.5" fill="none">
    <path d="M140 96 L102 96"/>
    <path d="M250 96 L212 96"/>
  </g>
  <polygon points="96,96 106,92 106,100" fill="#64748b"/>
  <polygon points="206,96 216,92 216,100" fill="#64748b"/>
  <text x="123" y="114" text-anchor="middle" font-size="12" font-weight="700" fill="#64748b">÷ 10</text>
  <text x="233" y="114" text-anchor="middle" font-size="12" font-weight="700" fill="#64748b">÷ 10</text>

  <text x="180" y="140" text-anchor="middle" font-size="14" font-weight="700" fill="#0f172a">1 dm = 10 cm = 100 mm</text>
</svg>`.trim();

// ── Figure Ex. 6b : on part de l'énoncé, on trace à la fin ──
// L'élève n'a qu'une LISTE DE MESURES sous les yeux, pas de figure. L'exemple
// doit donc partir de là et dérouler les 3 étapes de la règle dans le même
// ordre ; le segment n'apparaît qu'en conclusion, jamais en point de départ.
// JK = 2 cm : le générateur tire ses deux petites distances dans [3..8], donc
// hors de portée. Le mélange dm/cm est délibéré — un tirage sur trois le fait,
// et c'est là que l'élève se trompe.
const SVG_ALIGNEMENT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 218" role="img"
     aria-label="Méthode en trois étapes : convertir, additionner les deux plus petites, repérer la lettre commune. J, K, L sont alignés dans cet ordre.">
  <rect x="8" y="8" width="344" height="30" rx="6" fill="#F1F5F9"/>
  <text x="180" y="28" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">JK = 2 cm     KL = 1,1 dm     JL = 13 cm</text>

  <circle cx="24" cy="60" r="9" fill="#FFB938"/>
  <text x="24" y="64" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">1</text>
  <text x="42" y="58" font-size="12" fill="#475569">même unité</text>
  <text x="42" y="74" font-size="13" font-weight="700" fill="#F97316">1,1 dm = 11 cm</text>

  <circle cx="24" cy="102" r="9" fill="#FFB938"/>
  <text x="24" y="106" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">2</text>
  <text x="42" y="100" font-size="12" fill="#475569">les deux plus petites</text>
  <text x="42" y="116" font-size="13" font-weight="700" fill="#0f172a">2 + 11 = 13, la plus grande</text>

  <circle cx="24" cy="144" r="9" fill="#FFB938"/>
  <text x="24" y="148" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">3</text>
  <text x="42" y="142" font-size="12" fill="#475569">lettre commune à JK et KL</text>
  <text x="42" y="158" font-size="13" font-weight="700" fill="#F97316">K, donc K est situé entre J et L</text>

  <line x1="40" y1="188" x2="320" y2="188" stroke="#1E3A8A" stroke-width="2.5"/>
  <circle cx="40" cy="188" r="4.5" fill="#1E3A8A"/>
  <circle cx="83" cy="188" r="4.5" fill="#F97316"/>
  <circle cx="320" cy="188" r="4.5" fill="#1E3A8A"/>
  <text x="40" y="180" text-anchor="middle" font-size="13" fill="#0f172a">J</text>
  <text x="83" y="180" text-anchor="middle" font-size="13" font-weight="700" fill="#F97316">K</text>
  <text x="320" y="180" text-anchor="middle" font-size="13" fill="#0f172a">L</text>
  <text x="180" y="210" text-anchor="middle" font-size="13" font-weight="700" fill="#F97316">Alignés dans l'ordre J, K, L</text>
</svg>`.trim();

const CP_653 = {
    // Page unique (revue 2026-08-12) : la figure des trois notations parle
    // d'elle-même, la règle verbale n'ajoutait que du texte.
    notation_vf_tableau: {
        pages: [
            { titre: 'Un exemple', svg: SVG_NOTATIONS },
        ],
    },

    // Page unique (revue 2026-08-12) : les trois étapes dictées par
    // l'utilisateur, sans figure — la règle graduée est déjà sous les yeux
    // de l'élève dans l'exercice lui-même.
    mesurer_regle_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    '1. Lis la valeur correspondant à chaque extrémité du segment.',
                    '2. Ici, chaque petite graduation vaut **0,5**.',
                    '3. Pour trouver la distance, soustrais la plus petite valeur à la plus grande.',
                ],
            },
        ],
    },

    // Page unique (revue 2026-08-12) : le geste se fait dans l'exercice
    // lui-même (compas virtuel sous les yeux), la figure d'exemple n'ajoutait
    // rien.
    comparer_compas_tableau: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Clique sur les **deux extrémités** d\'un segment : le compas garde son écartement.',
                    'Recommence sur l\'autre segment, puis compare les deux écartements.',
                ],
            },
        ],
    },

    perimetre_compas_qcm_unique: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Clique sur **deux sommets voisins** : le côté se reporte sur la demi-droite.',
                    'Recommence pour **tous les côtés**. La longueur totale obtenue est le **périmètre** de la figure.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_PERIMETRE },
        ],
    },

    position_point_qcm_unique: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    // Aucun nom de point : le générateur tire ses lettres au hasard,
                    // nommer E/F/G ici les ferait diverger de l'exercice affiché.
                    'Fais un **schéma** : place les points où l\'énoncé le dit.',
                    'Le schéma permet de trouver le **calcul à faire**. Si la position d\'un point n\'est pas précisée, plusieurs réponses peuvent être possibles.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_POSITION },
        ],
    },

    alignement_qcm_unique: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Mets d\'abord toutes les mesures dans la **même unité**.',
                    'Additionne les **deux plus petites** longueurs. Si leur somme est égale à la plus grande, les points sont alignés.',
                    'Le point situé **entre les deux autres** est la lettre commune aux deux plus petites longueurs.',
                ],
                svg: SVG_CONVERSION,
            },
            { titre: 'Un exemple', svg: SVG_ALIGNEMENT },
        ],
    },
};

export default CP_653;
